import { formatOfferExpiration, getOfferHref, getOfferImageUrl } from './deals.js';
import { buildListingPath, getListingCategorySlug } from './listingUrls.js';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_LIMIT = 5;

export function parsePanelDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isWithinLastWeek(value, now = new Date()) {
  const parsed = parsePanelDate(value);
  if (!parsed) return false;
  const delta = now.getTime() - parsed.getTime();
  return delta >= 0 && delta <= WEEK_MS;
}

function getSortTime(value) {
  return parsePanelDate(value)?.getTime() || 0;
}

export function selectThisWeekThenLatest(rows, getDate, { now = new Date(), limit = DEFAULT_LIMIT } = {}) {
  const source = Array.isArray(rows) ? rows : [];
  const withIndex = source.map((row, index) => ({ row, index, date: getDate(row) }));

  const sortByDateDesc = (a, b) => {
    const diff = getSortTime(b.date) - getSortTime(a.date);
    return diff || a.index - b.index;
  };

  const fresh = withIndex.filter((item) => isWithinLastWeek(item.date, now)).sort(sortByDateDesc);
  const freshSet = new Set(fresh.map((item) => item.index));
  const fallback = withIndex.filter((item) => !freshSet.has(item.index)).sort(sortByDateDesc);

  return [...fresh, ...fallback].slice(0, limit).map((item) => item.row);
}

export function getInitials(value) {
  const parts = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

export function formatRelativeAge(value, now = new Date()) {
  const date = parsePanelDate(value);
  if (!date) return 'Recently added';

  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return 'Recently added';
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  const weeks = Math.floor(days / 7);
  return `${weeks} weeks ago`;
}

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function getListingLocation(listing = {}) {
  return cleanString(listing.city)
    || cleanString(listing.address?.city)
    || cleanString(listing.marketName)
    || cleanString(listing.address)
    || '';
}

function getListingCategoryName(listing = {}) {
  return cleanString(listing.listingCategories?.[0]?.tenantCategory?.name)
    || cleanString(listing.listingCategories?.[0]?.category?.name)
    || cleanString(listing.primaryCategory?.name)
    || cleanString(listing.category?.name)
    || cleanString(listing.category)
    || cleanString(getListingCategorySlug(listing).replace(/-/g, ' '));
}

export function buildDealPanelRows(offers, options = {}) {
  return selectThisWeekThenLatest(
    offers,
    (offer) => offer?.publishedAt || offer?.createdAt,
    options
  ).map((offer) => {
    const title = cleanString(offer?.listing?.name) || cleanString(offer?.title) || 'Local Business';
    const subtitle = cleanString(offer?.title) || cleanString(offer?.shortDescription) || 'Current local offer';
    const badge = cleanString(offer?.offerType).replaceAll('_', ' ') || 'Special Offer';
    const expires = formatOfferExpiration(offer?.validThrough || offer?.endAt || offer?.endDate || offer?.expiresAt || offer?.validUntil, options.locale);

    return {
      id: cleanString(offer?.id) || cleanString(offer?.slug) || title,
      href: getOfferHref(offer),
      imageUrl: getOfferImageUrl(offer),
      badge,
      title,
      subtitle,
      expires,
      fallbackInitials: getInitials(title),
    };
  });
}

export function buildNewBusinessPanelRows(listings, options = {}) {
  return selectThisWeekThenLatest(
    listings,
    (listing) => listing?.createdAt || listing?.updatedAt,
    options
  ).map((listing) => {
    const title = cleanString(listing?.name) || 'Local Business';
    const category = getListingCategoryName(listing);
    const location = getListingLocation(listing);
    const subtitle = [category, location].filter(Boolean).join(' | ') || 'Recently added business';

    return {
      id: cleanString(listing?.id) || cleanString(listing?.slug) || title,
      href: buildListingPath(listing) || '/listings',
      listing,
      cardListing: {
        id: cleanString(listing?.id),
        name: title,
        category,
        city: location,
        initials: getInitials(title),
        createdAt: listing?.createdAt || listing?.updatedAt || '',
        ageLabel: formatRelativeAge(listing?.createdAt || listing?.updatedAt, options.now),
        tier: listing?.tier || listing?.planTier || listing?.listingPlan?.tier?.key || '',
      },
      avatar: getInitials(title),
      title,
      subtitle,
      age: formatRelativeAge(listing?.createdAt || listing?.updatedAt, options.now),
    };
  });
}

export function buildEmptyPanelRow({ id, title, subtitle, href }) {
  return {
    id,
    title,
    subtitle,
    href,
    isEmpty: true,
  };
}
